import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './SearchBar.css';

function SearchBar({ onItemSelect }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [showNetSuiteLookup, setShowNetSuiteLookup] = useState(false);
  const [lookingUpNetSuite, setLookingUpNetSuite] = useState(false);
  const [fetchingItemDetails, setFetchingItemDetails] = useState(false);
  const searchRef = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchItems = async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      setShowNetSuiteLookup(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(`/api/search?query=${searchQuery}`);
      const results = Array.isArray(response.data) ? response.data : [];
      setSuggestions(results);
      setShowSuggestions(true);
      setSelectedIndex(-1);

      // Show NetSuite lookup option if no results found and query looks like a SKU
      if (results.length === 0 && searchQuery.length >= 3) {
        setShowNetSuiteLookup(true);
      } else {
        setShowNetSuiteLookup(false);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setSuggestions([]);
      setShowNetSuiteLookup(false);
    } finally {
      setIsSearching(false);
    }
  };

  const lookupInNetSuite = async () => {
    setLookingUpNetSuite(true);
    setShowNetSuiteLookup(false);

    try {
      const response = await axios.get(`/api/lookup/${encodeURIComponent(query)}`);

      if (response.data.item) {
        // Item found or added
        setSuggestions([response.data.item]);
        setShowSuggestions(true);
        setSelectedIndex(0);

        if (response.data.added) {
          console.log('Item added from NetSuite:', response.data.item.sku);
        }
      } else {
        // Item not found in NetSuite
        alert(`Item "${query}" not found in NetSuite`);
      }
    } catch (error) {
      console.error('NetSuite lookup failed:', error);
      alert('Failed to lookup item in NetSuite');
    } finally {
      setLookingUpNetSuite(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      searchItems(value);
    }, 300);
  };

  const handleItemClick = async (item) => {
    setQuery(item.sku);
    setShowSuggestions(false);
    setFetchingItemDetails(true);

    // Fetch fresh case_qty from NetSuite when item is selected
    try {
      const response = await axios.get(`/api/item/${item.sku}`);
      if (response.data) {
        // Use the item with fresh case_qty from NetSuite
        onItemSelect(response.data);
      } else {
        // Fallback to original item if fetch fails
        onItemSelect(item);
      }
    } catch (error) {
      console.error('Failed to fetch fresh item data:', error);
      // Fallback to original item if fetch fails
      onItemSelect(item);
    } finally {
      setFetchingItemDetails(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleItemClick(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className="search-bar" ref={searchRef}>
      <input
        type="text"
        placeholder="Search by SKU or description..."
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        className="search-input"
        disabled={lookingUpNetSuite || fetchingItemDetails}
      />

      {fetchingItemDetails && (
        <div className="suggestions">
          <div className="netsuite-lookup-message">
            Fetching item details...
          </div>
        </div>
      )}

      {lookingUpNetSuite && (
        <div className="suggestions">
          <div className="netsuite-lookup-message">
            Looking up in NetSuite...
          </div>
        </div>
      )}

      {showNetSuiteLookup && !lookingUpNetSuite && (
        <div className="suggestions">
          <div className="no-results-message">
            No results found in local database
          </div>
          <div
            className="netsuite-lookup-option"
            onClick={lookupInNetSuite}
          >
            <div className="lookup-action">
              🔍 Search in NetSuite for "{query}"
            </div>
            <div className="lookup-hint">
              Click to fetch from NetSuite and add to database
            </div>
          </div>
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && !lookingUpNetSuite && (
        <div className="suggestions">
          {suggestions.map((item, index) => (
            <div
              key={item.sku}
              className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="suggestion-sku">{item.sku}</div>
              <div className="suggestion-description">{item.description}</div>
              {item.collection && (
                <div className="suggestion-collection">{item.collection}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchBar;